/**
 * Turning one value into one safe CSV cell — pure, and in its own module.
 *
 * Kept import-free for the same reason as `bookingReserved.ts`: `csvExport.ts`
 * pulls in the Redux store and through it Auth0, so anything living there
 * cannot be unit-tested at all (the suite dies on `TextEncoder is not
 * defined` before a single case runs). A security guard that cannot be tested
 * is a security guard nobody can show works.
 */

/**
 * Characters that make a spreadsheet treat a cell as a formula rather than
 * text. A leading tab or carriage return counts: Excel strips it and then
 * evaluates what follows.
 */
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r'];

/** A value a spreadsheet should keep as a number (balances, totals). */
const isPlainNumber = (value: string): boolean => /^-?\d+([.,]\d+)?$/.test(value.trim());

/**
 * CSV injection guard (CWE-1236).
 *
 * The bookings export carries fields the CUSTOMER typed — name, company,
 * address, phone and the free-text booking comment — into a file the tenant
 * opens in Excel. A comment of `=HYPERLINK("http://…","Rechnung")`, or the
 * `@SUM(1+9)*cmd|…!A0` DDE form, is a formula the moment the file is opened.
 * Quoting it as a CSV field does not help: the danger is what the spreadsheet
 * does with the value *after* parsing it.
 *
 * The standard mitigation is a leading apostrophe, which marks the cell as
 * text and is not displayed. Numbers are exempt so the balance and total
 * columns stay arithmetic — otherwise `-5.00` would become text and every sum
 * in the sheet would silently skip it.
 */
export const neutralizeFormula = (value: string): string => {
    if (value.length === 0 || isPlainNumber(value)) return value;
    return FORMULA_TRIGGERS.includes(value[0]) ? `'${value}` : value;
};

/** One value → one CSV cell: formula-neutralised, then quoted if it has to be. */
export const escapeCSVField = (value: string): string => {
    const safe = neutralizeFormula(value);
    if (safe.includes(';') || safe.includes('"') || safe.includes('\n') || safe.includes('\r')) {
        return `"${safe.replace(/"/g, '""')}"`;
    }
    return safe;
};
